import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
    BimDataModel,
    ColumnFilter,
    DataTableColumnData,
    DatatableComponent,
    DataTableConfig,
    DataTableObservableDataSource, getErrorMessage,
    ProjectApiService,
    TableAction
} from '@cas-hub/acabim-common-client';
import {MessageService} from 'primeng/api';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {BimModelAdministrationService} from '../../../../_services/bim/bim-model-administration.service';

@Component({
    selector: 'acabim-admin-view-bim-models',
    templateUrl: './view-bim-models.component.html',
    styleUrls: ['./view-bim-models.component.scss']
})
export class ViewBimModelsComponent extends DataTableObservableDataSource<BimDataModel> implements OnInit, OnDestroy {

    private static readonly UpdateModelActionId = 0;
    private static readonly DeleteModelActionId = 1;
    private static readonly ChangeDefaultModelActionId = 2;

    @ViewChild('dataTable', {static: false})
    public set dataTable(val: DatatableComponent | undefined) {
        this._datatable = val;
        this.selectedData = []; // Fix selected data bug
        if (this._datatableRefreshRequired && this._datatable) {
            this._datatable.requestImmediateDataRefresh();
            this._datatableRefreshRequired = false;
        }
    }

    public showUpdateModel = false;
    public showProgress = false;

    public columnFilters: ColumnFilter[] = [];
    public readonly actions: TableAction[] = [
        {id: ViewBimModelsComponent.UpdateModelActionId, name: 'Update Model'},
        {id: ViewBimModelsComponent.DeleteModelActionId, name: 'Delete Model'},
        {id: ViewBimModelsComponent.ChangeDefaultModelActionId, name: 'Set as default model'}
    ];

    public readonly columnData: DataTableColumnData[] = [
        {displayName: 'Name', objectPropertyName: 'modelName'},
        {displayName: 'Model Type', objectPropertyName: 'modelType'},
        {displayName: 'Phase', objectPropertyName: 'modelPhase'},
        {displayName: 'Status', objectPropertyName: 'modelState'},
        {displayName: 'Default', objectPropertyName: 'isDefault'},
        {displayName: 'Created By', objectPropertyName: 'createdUser.name'}
    ];

    public readonly config: DataTableConfig = {
        globalFilterFields: [],
        hasActions: true,
        hasCheckbox: true,
        hasDetailsButton: false,
        hasFilters: false,
        isExportEnabled: false,
        isLazyLoadingEnabled: false,
        isPaginationEnabled: false,
        isSearchEnabled: false,
        searchPlaceHolder: '',
        emptyMessage: 'No building models found for the selected project',
        tableHeading: 'BIM Models',
        datatableUpdateId: 'BimDataModels'
    };

    private _datatableRefreshRequired = false; // To circumvent a race condition that can occur if the data table is not visible due to progress
    private _datatable?: DatatableComponent;

    private readonly _ngUnsubscribe = new Subject<void>();

    constructor(public readonly projectApiService: ProjectApiService,
                private readonly bimModelService: BimModelAdministrationService,
                private readonly messageService: MessageService) {
        super(() => {
            if (projectApiService.selectedProjectId) {
                return bimModelService.getBimModels(projectApiService.selectedProjectId);
            }

            return null;
        });
    }

    public ngOnInit(): void {
        this.projectApiService.selectedProjectId$
            .pipe(takeUntil(this._ngUnsubscribe))
            .subscribe(_ => this.onRequestToRefreshData());
    }

    public ngOnDestroy(): void {
        this._ngUnsubscribe.next();
        this._ngUnsubscribe.complete();
    }

    public onAction(action: TableAction): void {
        if (this.selectedData.length === 0) {
            this.messageService.add({detail: `Please select a model to ${action.name}`, severity: 'error'});
        } else if (this.selectedData.length > 1) {
            this.messageService.add({detail: `Please select a single model to ${action.name}`, severity: 'error'});
        } else {
            const refreshDataTable = (): void => { // function to refresh the data table
                this.showProgress = false;
                this.selectedData = [];
                if (this._datatable) { // we hide the datatable with the progress bar, sometime if may not have initialised yet
                    this._datatable.requestImmediateDataRefresh();
                } else {
                    this._datatableRefreshRequired = true;
                }
            };

            switch (action.id) {
                case ViewBimModelsComponent.UpdateModelActionId:
                    this.showUpdateModel = true;
                    break;

                case ViewBimModelsComponent.DeleteModelActionId:
                    const model = this.selectedData[0];
                    if (model.isDefault) {
                        this.messageService.add({detail: 'Unable to delete default BIM model', severity: 'error'});
                    } else if (model.modelState === 'Initialising' || model.modelState === 'Updating') {
                        this.messageService.add({detail: 'Unable to delete an updating or initialising model', severity: 'error'});
                    } else {
                        this.showProgress = true;
                        this.bimModelService.deleteBimModel(model.modelId).subscribe(_ => {
                                refreshDataTable();
                            }, _ => {
                                this.showProgress = false;
                            }, () => this.showProgress = false);
                    }
                    break;

                case ViewBimModelsComponent.ChangeDefaultModelActionId:
                    const newDefaultModel = this.selectedData[0];
                    this.showProgress = true;
                    if (this.projectApiService.selectedProjectId) {
                        this.bimModelService.changeDefaultBimModel(this.projectApiService.selectedProjectId, newDefaultModel.modelId)
                            .subscribe(_ => {
                                refreshDataTable();
                            }, error => {
                                this.showProgress = false;
                                this.messageService.add(getErrorMessage(error, 'Unable to change default model'));
                            });
                    }
                    break;
            }
        }
    }

    public onDataExpanded(value: BimDataModel): void {
    }

    public onDataFiltered(): void {
    }

    public onNewModelCreated(model: BimDataModel): void {
        this.rowData.push(model);
    }

    public onModelUpdated(status: boolean): void {
        this.showUpdateModel = false;
        if (status) {
            this.dataTable?.requestImmediateDataRefresh();
        }
    }
}
